import React, { Fragment, FunctionComponent } from 'react';
import { APIReference_apiReference_ClassReference_method_parameter as ClassReferenceParameters } from '@modules/client';
import { Typography } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import StandardMarkdownNoMargin from '@modules/ui/markdown/components/StandardMarkdownNoMargin';
import ContentTable from '@modules/ui/shared/components/ContentTable';
import useParameterReturnTableStyles from './ParameterReturnTable.styles';
import LinkableTypeReference from './LinkableTypeReference';

interface ParameterTableProps {
  parameters: ClassReferenceParameters[] | null;
}

const ParameterTable: FunctionComponent<React.PropsWithChildren<ParameterTableProps>> = ({
  parameters,
}) => {
  const { translate } = useTranslation();
  const {
    classes: { title },
  } = useParameterReturnTableStyles();
  if (!parameters || parameters.length < 1) {
    return null;
  }

  return (
    <Fragment>
      <Typography className={title} variant='h4' component='h4'>
        {translate('Label.Parameters') || 'Parameters'}
      </Typography>
      <ContentTable
        data={parameters}
        renderer={(row) => {
          const { type, name, summary, default: defaultValue } = row as ClassReferenceParameters;
          const hasDefaultValue =
            defaultValue !== null &&
            defaultValue.toString().trim().length !== 0 &&
            defaultValue !== 'null';

          return {
            name: (
              <Fragment>
                <Typography variant='body2'>{name}</Typography>
                <Typography variant='body2'>: </Typography>
                <LinkableTypeReference type={type} variant='body2' />
              </Fragment>
            ),
            summary: (summary || hasDefaultValue) && (
              <Fragment>
                <StandardMarkdownNoMargin>{summary}</StandardMarkdownNoMargin>
                {hasDefaultValue ? (
                  <Typography>
                    {translate('Label.DefaultValue', { defaultValue }) ||
                      `Default value: ${defaultValue}`}
                  </Typography>
                ) : null}
              </Fragment>
            ),
          };
        }}
      />
    </Fragment>
  );
};

export default ParameterTable;
